import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaBolt,
  FaBrain,
  FaBullseye,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaLayerGroup,
  FaMapMarkedAlt,
  FaNetworkWired,
  FaPhoneAlt,
  FaPlayCircle,
  FaRocket,
  FaShieldAlt,
  FaSlidersH,
  FaThLarge,
  FaUsers
} from "react-icons/fa";
import { api, resolveAssetUrl } from "../../api";
import "./commercial-landing.css";

const MotionSection = motion.create("section");

const iconMap = {
  bolt: FaBolt,
  brain: FaBrain,
  check: FaCheckCircle,
  clock: FaClock,
  grid: FaThLarge,
  layers: FaLayerGroup,
  monitor: FaMapMarkedAlt,
  network: FaNetworkWired,
  pulse: FaUsers,
  rocket: FaRocket,
  settings: FaSlidersH,
  shield: FaShieldAlt,
  spark: FaPlayCircle,
  target: FaBullseye
};

const emptyLead = {
  nome: "",
  empresa: "",
  telefone: "",
  email: "",
  segmento: "",
  quantidade_estimada: "",
  interesse_principal: "",
  plano_desejado: "",
  observacoes: ""
};

const setHeadValue = (selector, attribute, value) => {
  if (typeof document === "undefined" || !value) return;
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    const match = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (match) {
      node.setAttribute(match[1], match[2]);
    }
    document.head.appendChild(node);
  }
  node.setAttribute(attribute, value);
};

const ensureCanonical = (href) => {
  if (typeof document === "undefined" || !href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const toPublicSections = (bundle) => bundle?.sections || [];

const sectionByKey = (sections, key) => sections.find((section) => section.section_key === key);

const renderIcon = (name) => {
  const Icon = iconMap[name] || FaCheckCircle;
  return <Icon aria-hidden="true" />;
};

const formatPrice = (value) => {
  if (value == null || value === "") return null;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
};

const extractUtm = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
    referrer_url: document.referrer || "",
    source_url: window.location.href
  };
};

const CommercialLandingPage = () => {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [formStep, setFormStep] = useState(0);
  const [formState, setFormState] = useState({ submitting: false, success: "", error: "" });

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/public/landing")
      .then((response) => {
        if (!active) return;
        setBundle(response.data || null);
        setError("");
      })
      .catch(() => {
        if (!active) return;
        setError("Não foi possível carregar o conteúdo da landing.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const site = useMemo(() => bundle?.site || {}, [bundle]);
  const sections = useMemo(() => toPublicSections(bundle), [bundle]);
  const hero = sectionByKey(sections, "hero");
  const about = sectionByKey(sections, "about");
  const benefits = sectionByKey(sections, "benefits");
  const ai = sectionByKey(sections, "ai");
  const howItWorks = sectionByKey(sections, "how_it_works");
  const gallery = sectionByKey(sections, "gallery");
  const plans = sectionByKey(sections, "plans");
  const leadSection = sectionByKey(sections, "lead_form");
  const onboarding = sectionByKey(sections, "onboarding");
  const testimonials = sectionByKey(sections, "testimonials");
  const faq = sectionByKey(sections, "faq");
  const finalCta = sectionByKey(sections, "final_cta");

  const navSections = sections.filter((section) =>
    ["about", "benefits", "ai", "how_it_works", "gallery", "plans", "faq"].includes(section.section_key)
  );

  const faqStructuredData = useMemo(() => {
    if (!faq?.items?.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.items.map((item) => ({
        "@type": "Question",
        name: item.title,
        acceptedAnswer: { "@type": "Answer", text: item.description }
      }))
    };
  }, [faq]);

  useEffect(() => {
    if (!site?.site_name) return;
    document.title = site.meta_title || site.site_name;
    setHeadValue('meta[name="description"]', "content", site.meta_description || "");
    setHeadValue('meta[property="og:title"]', "content", site.og_title || site.meta_title || site.site_name);
    setHeadValue(
      'meta[property="og:description"]',
      "content",
      site.og_description || site.meta_description || ""
    );
    setHeadValue('meta[property="og:image"]', "content", resolveAssetUrl(site.og_image_url || ""));
    setHeadValue('meta[property="og:type"]', "content", "website");
    ensureCanonical(typeof window !== "undefined" ? window.location.href : "");
  }, [site]);

  const handleLeadField = (field, value) => {
    setLeadForm((current) => ({ ...current, [field]: value }));
  };

  const jumpToLead = (planSlug = "") => {
    if (planSlug) {
      setLeadForm((current) => ({ ...current, plano_desejado: planSlug }));
    }
    const target = document.getElementById("pré-cadastro");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validateStep = () => {
    if (formStep === 0) {
      return leadForm.nome.trim() && leadForm.telefone.trim() && leadForm.email.trim();
    }
    if (formStep === 1) {
      return leadForm.segmento.trim() && leadForm.interesse_principal.trim();
    }
    return true;
  };

  const submitLead = async (event) => {
    event.preventDefault();
    setFormState({ submitting: true, success: "", error: "" });

    try {
      const response = await api.post("/public/landing/leads", {
        ...leadForm,
        ...extractUtm()
      });
      setFormState({
        submitting: false,
        success: response.data?.success_message || "Pre-cadastro enviado com sucesso.",
        error: ""
      });
      setLeadForm(emptyLead);
      setFormStep(0);

      if (response.data?.checkout_url && typeof window !== "undefined") {
        window.open(response.data.checkout_url, "_blank", "noopener,noreferrer");
      }
    } catch (requestError) {
      setFormState({
        submitting: false,
        success: "",
        error: requestError?.response?.data?.error || "Não foi possível enviar o pré-cadastro."
      });
    }
  };

  const heroStats = hero?.items || [];
  const planOptions = plans?.items || [];

  if (loading) {
    return <div className="landing-loading">Carregando experiência comercial...</div>;
  }

  if (error) {
    return <div className="landing-loading">{error}</div>;
  }

  return (
    <div
      className="landing-shell"
      style={{
        "--landing-primary": site.primary_color || "#123447",
        "--landing-secondary": site.secondary_color || "#0f4c5c",
        "--landing-accent": site.accent_color || "#f59e0b"
      }}
    >
      {faqStructuredData ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
        />
      ) : null}

      <header className="landing-topbar">
        <div className="landing-brand">
          <span className="landing-brand-mark" />
          <div>
            <strong>{site.site_name || "EU MONITORO"}</strong>
            <span>{site.settings_json?.hero_trust_label || "Monitoramento inteligente e administrável"}</span>
          </div>
        </div>
        <nav className="landing-nav" aria-label="Navegação da landing">
          {navSections.map((section) => (
            <a key={section.section_key} href={`#${section.slug || section.section_key}`}>
              {section.label || section.title}
            </a>
          ))}
        </nav>
        <button className="landing-primary-btn" type="button" onClick={() => jumpToLead()}>
          Solicitar demonstração
        </button>
      </header>

      <main>
        {hero ? (
          <MotionSection className="landing-hero" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="landing-hero-copy">
              <span className="landing-eyebrow">{hero.eyebrow}</span>
              <h1>{hero.title}</h1>
              {hero.subtitle ? <p className="landing-subtitle">{hero.subtitle}</p> : null}
              {hero.description ? <p className="landing-description">{hero.description}</p> : null}
              <div className="landing-cta-row">
                <button className="landing-primary-btn" type="button" onClick={() => jumpToLead()}>
                  {hero.primary_cta_label || "Solicitar demonstração"}
                </button>
                <a className="landing-secondary-btn" href={hero.secondary_cta_url || "#planos"}>
                  {hero.secondary_cta_label || "Ver planos"}
                </a>
              </div>
              <div className="landing-stat-grid">
                {heroStats.map((item) => (
                  <div key={item.id || item.slug} className="landing-stat-card">
                    <span className="landing-icon">{renderIcon(item.icon)}</span>
                    <strong>{item.title}</strong>
                    <span>{item.description || item.subtitle}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="landing-hero-media">
              <div className="landing-hero-media-frame">
                <img
                  src={resolveAssetUrl(hero.media_url || site.og_image_url)}
                  alt={hero.media_alt || hero.title}
                  loading="eager"
                />
              </div>
            </div>
          </MotionSection>
        ) : null}

        {[about, benefits, ai, howItWorks, gallery, plans, leadSection, onboarding, testimonials, faq, finalCta]
          .filter(Boolean)
          .map((section) => {
            if (section.section_key === "hero") return null;

            if (section.section_key === "about") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                    {section.description ? <p className="landing-description">{section.description}</p> : null}
                  </div>
                  <div className="landing-feature-grid">
                    {section.items.map((item) => (
                      <article key={item.id || item.slug} className="landing-feature-card">
                        <span className="landing-icon">{renderIcon(item.icon)}</span>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </article>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "benefits") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section landing-section-alt">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                  </div>
                  <div className="landing-benefit-grid">
                    {section.items.map((item) => (
                      <article key={item.id || item.slug} className="landing-benefit-card">
                        <span className="landing-chip">{item.badge}</span>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </article>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "ai") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section landing-ai">
                  <div className="landing-ai-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    <p className="landing-subtitle">{section.subtitle}</p>
                    <p className="landing-description">{section.description}</p>
                    <button className="landing-primary-btn" type="button" onClick={() => jumpToLead()}>
                      {section.primary_cta_label || "Solicitar demonstração"}
                    </button>
                  </div>
                  <div className="landing-ai-visual">
                    <img src={resolveAssetUrl(section.media_url)} alt={section.media_alt || section.title} loading="lazy" />
                    <div className="landing-ai-card-stack">
                      {section.items.map((item) => (
                        <article key={item.id || item.slug} className="landing-ai-card">
                          <span className="landing-icon">{renderIcon(item.icon)}</span>
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                        </article>
                      ))}
                    </div>
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "how_it_works" || section.section_key === "onboarding") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                  </div>
                  <div className="landing-step-grid">
                    {section.items.map((item, itemIndex) => (
                      <article key={item.id || item.slug} className="landing-step-card">
                        <span className="landing-step-index">{String(itemIndex + 1).padStart(2, "0")}</span>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </article>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "gallery") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section landing-section-alt">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                  </div>
                  <div className="landing-gallery-grid">
                    {section.items.map((item) => (
                      <figure key={item.id || item.slug} className="landing-gallery-card">
                        <img src={resolveAssetUrl(item.image_url)} alt={item.title} loading="lazy" />
                        <figcaption>
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "plans") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                  </div>
                  <div className="landing-plan-grid">
                    {section.items.map((item) => (
                      <article key={item.id || item.slug} className={`landing-plan-card ${item.highlight ? "is-highlighted" : ""}`}>
                        <span className="landing-chip">{item.badge || "Plano"}</span>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                        <div className="landing-plan-price">
                          {item.price_label ? <span>{item.price_label}</span> : null}
                          <strong>{formatPrice(item.price_value) || item.price_label || "Sob consulta"}</strong>
                          {item.price_value != null && item.price_suffix ? <span>{item.price_suffix}</span> : null}
                        </div>
                        <ul>
                          {(item.list_json || []).map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                        <button className="landing-primary-btn" type="button" onClick={() => jumpToLead(item.slug)}>
                          {item.cta_label || "Quero este plano"} <FaArrowRight />
                        </button>
                      </article>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "lead_form") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section landing-form-section">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                    {section.description ? <p className="landing-description">{section.description}</p> : null}
                  </div>
                  <form className="landing-form-card" onSubmit={submitLead}>
                    <div className="landing-form-progress">
                      {[0, 1, 2].map((step) => (
                        <span key={step} className={step <= formStep ? "is-active" : ""} />
                      ))}
                    </div>

                    {formStep === 0 ? (
                      <div className="landing-form-grid">
                        <label><span>Nome</span><input value={leadForm.nome} onChange={(event) => handleLeadField("nome", event.target.value)} required /></label>
                        <label><span>Empresa</span><input value={leadForm.empresa} onChange={(event) => handleLeadField("empresa", event.target.value)} /></label>
                        <label><span>Telefone / WhatsApp</span><input value={leadForm.telefone} onChange={(event) => handleLeadField("telefone", event.target.value)} required /></label>
                        <label><span>E-mail</span><input type="email" value={leadForm.email} onChange={(event) => handleLeadField("email", event.target.value)} required /></label>
                      </div>
                    ) : null}

                    {formStep === 1 ? (
                      <div className="landing-form-grid">
                        <label><span>Segmento</span><input value={leadForm.segmento} onChange={(event) => handleLeadField("segmento", event.target.value)} required /></label>
                        <label><span>Quantidade estimada</span><input value={leadForm.quantidade_estimada} onChange={(event) => handleLeadField("quantidade_estimada", event.target.value)} /></label>
                        <label className="landing-form-full"><span>Interesse principal</span><input value={leadForm.interesse_principal} onChange={(event) => handleLeadField("interesse_principal", event.target.value)} required /></label>
                        <label className="landing-form-full"><span>Plano desejado</span><select value={leadForm.plano_desejado} onChange={(event) => handleLeadField("plano_desejado", event.target.value)}><option value="">Selecionar plano</option>{planOptions.map((item) => <option key={item.id || item.slug} value={item.slug}>{item.title}</option>)}</select></label>
                      </div>
                    ) : null}

                    {formStep === 2 ? (
                      <div className="landing-form-grid">
                        <label className="landing-form-full"><span>Observacoes</span><textarea rows="5" value={leadForm.observacoes} onChange={(event) => handleLeadField("observacoes", event.target.value)} /></label>
                      </div>
                    ) : null}

                    {formState.error ? <p className="landing-form-error">{formState.error}</p> : null}
                    {formState.success ? <p className="landing-form-success">{formState.success}</p> : null}

                    <div className="landing-form-actions">
                      {formStep > 0 ? (
                        <button className="landing-secondary-btn" type="button" onClick={() => setFormStep((current) => current - 1)}>
                          Voltar
                        </button>
                      ) : <span />}

                      {formStep < 2 ? (
                        <button className="landing-primary-btn" type="button" onClick={() => validateStep() && setFormStep((current) => current + 1)} disabled={!validateStep()}>
                          Continuar
                        </button>
                      ) : (
                        <button className="landing-primary-btn" type="submit" disabled={formState.submitting}>
                          {formState.submitting ? "Enviando..." : section.metadata_json?.submit_label || "Enviar pré-cadastro"}
                        </button>
                      )}
                    </div>
                  </form>
                </MotionSection>
              );
            }

            if (section.section_key === "testimonials") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                  </div>
                  <div className="landing-quote-grid">
                    {section.items.map((item) => (
                      <blockquote key={item.id || item.slug} className="landing-quote-card">
                        <span className="landing-chip">{item.badge || "Depoimento"}</span>
                        <p>{item.description}</p>
                        <strong>{item.title}</strong>
                        <span>{item.subtitle}</span>
                      </blockquote>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            if (section.section_key === "faq") {
              return (
                <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-section landing-section-alt">
                  <div className="landing-section-copy">
                    <span className="landing-eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                  </div>
                  <div className="landing-faq-list">
                    {section.items.map((item) => (
                      <details key={item.id || item.slug} className="landing-faq-item">
                        <summary>{item.title}</summary>
                        <p>{item.description}</p>
                      </details>
                    ))}
                  </div>
                </MotionSection>
              );
            }

            return (
              <MotionSection key={section.section_key} id={section.slug || section.section_key} className="landing-final-cta">
                <div>
                  <span className="landing-eyebrow">{section.eyebrow}</span>
                  <h2>{section.title}</h2>
                  {section.subtitle ? <p className="landing-subtitle">{section.subtitle}</p> : null}
                  {section.description ? <p className="landing-description">{section.description}</p> : null}
                </div>
                <div className="landing-cta-row">
                  <button className="landing-primary-btn" type="button" onClick={() => jumpToLead()}>
                    {section.primary_cta_label || "Solicitar demonstração"}
                  </button>
                  <button className="landing-secondary-btn" type="button" onClick={() => jumpToLead()}>
                    {section.secondary_cta_label || "Falar com o time"}
                  </button>
                </div>
              </MotionSection>
            );
          })}
      </main>

      <footer className="landing-footer">
        <div>
          <strong>{site.site_name || "EU MONITORO"}</strong>
          <p>{site.footer_text}</p>
        </div>
        <div className="landing-footer-contact">
          {site.support_email ? <a href={`mailto:${site.support_email}`}><FaEnvelope /> {site.support_email}</a> : null}
          {site.support_phone ? <a href={`tel:${site.support_phone}`}><FaPhoneAlt /> {site.support_phone}</a> : null}
        </div>
      </footer>
    </div>
  );
};

export default CommercialLandingPage;

